import React from 'react';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const StockPerformanceChart = ({ 
  title, 
  labels, 
  labelTimestamps = [],
  prices, 
  volumes, 
  tradeMarkers = [],
  currency, 
  sma50, 
  sma200, 
  rsi, 
  dailyPercentageChange, 
  bollingerBands,
  macd,
  stochastic,
  support,
  resistance,
  atrSeries,
  adxSeries,
  obvSeries,
  showSMA50, 
  showSMA200, 
  showRSI, 
  showPriceChange,
  showBollingerBands,
  showMACD,
  showStochastic,
  showSupportResistance,
  showATR,
  showADX,
  showOBV,
  showVolumeAnalysis,
  showVolume = true,
  showPrice = true
}) => {
  const normalizedLabelTimestamps = Array.isArray(labelTimestamps)
    ? labelTimestamps.map((ts) => (Number.isFinite(ts) ? ts : null))
    : [];

  const inferredStepMs = normalizedLabelTimestamps.length > 1
    ? normalizedLabelTimestamps
        .slice(1)
        .map((ts, idx) => {
          const prev = normalizedLabelTimestamps[idx];
          return (ts && prev) ? Math.abs(ts - prev) : null;
        })
        .find((gap) => Number.isFinite(gap) && gap > 0) || null
    : null;

  const resolveClosestLabelIndex = (timestampMs) => {
    if (!Number.isFinite(timestampMs) || !normalizedLabelTimestamps.length) {
      return -1;
    }
    let closestIndex = -1;
    let minDiff = Number.POSITIVE_INFINITY;
    for (let i = 0; i < normalizedLabelTimestamps.length; i++) {
      const labelTs = normalizedLabelTimestamps[i];
      if (!Number.isFinite(labelTs)) continue;
      const diff = Math.abs(labelTs - timestampMs);
      if (diff < minDiff) {
        minDiff = diff;
        closestIndex = i;
      }
    }
    if (closestIndex < 0) return -1;
    const maxAllowedGap = inferredStepMs ? Math.max(inferredStepMs * 0.8, 60 * 1000) : Number.POSITIVE_INFINITY;
    return minDiff <= maxAllowedGap ? closestIndex : -1;
  };

  const buyMarkerData = labels.map(() => null);
  const sellMarkerData = labels.map(() => null);
  const buyMarkerMetaByIndex = {};
  const sellMarkerMetaByIndex = {};

  (Array.isArray(tradeMarkers) ? tradeMarkers : []).forEach((marker) => {
    const index = resolveClosestLabelIndex(marker?.timestampMs);
    if (index < 0) return;
    const fallbackPrice = Number.isFinite(prices?.[index]) ? prices[index] : null;
    const markerPrice = Number.isFinite(marker?.price) ? marker.price : fallbackPrice;
    if (!Number.isFinite(markerPrice)) return;

    const markerMeta = {
      tradeId: marker.tradeId,
      timeLabel: marker.timeLabel || '',
      price: markerPrice,
    };
    if (marker?.type === 'BUY') {
      buyMarkerData[index] = markerPrice;
      buyMarkerMetaByIndex[index] = markerMeta;
    } else if (marker?.type === 'SELL') {
      sellMarkerData[index] = markerPrice;
      sellMarkerMetaByIndex[index] = markerMeta;
    }
  });

  const hasBuyMarkers = buyMarkerData.some((value) => Number.isFinite(value));
  const hasSellMarkers = sellMarkerData.some((value) => Number.isFinite(value));

  // Main Price & Volume Chart Data
  const mainChartDatasets = [
    (showPrice && {
      type: 'line',
      label: 'Price',
      borderColor: '#007bff',
      backgroundColor: '#007bff',
      data: prices,
      yAxisID: 'y',
      tension: 0.1,
      pointRadius: 0,
      fill: false,
    }),
    (hasBuyMarkers && {
      type: 'line',
      label: 'Buy Time',
      borderColor: '#198754',
      backgroundColor: '#198754',
      data: buyMarkerData,
      yAxisID: 'y',
      showLine: false,
      pointRadius: 5,
      pointHoverRadius: 6,
      pointStyle: 'triangle',
      tradeMetaByIndex: buyMarkerMetaByIndex,
    }),
    (hasSellMarkers && {
      type: 'line',
      label: 'Sell Time',
      borderColor: '#dc3545',
      backgroundColor: '#dc3545',
      data: sellMarkerData,
      yAxisID: 'y',
      showLine: false,
      pointRadius: 5,
      pointHoverRadius: 6,
      pointStyle: 'rectRot',
      tradeMetaByIndex: sellMarkerMetaByIndex,
    }),
    (showSMA50 && {
      type: 'line',
      label: 'SMA 50',
      borderColor: '#ffc107',
      backgroundColor: '#ffc107',
      data: sma50,
      yAxisID: 'y',
      tension: 0.1,
      pointRadius: 0,
      fill: false,
    }),
    (showSMA200 && {
      type: 'line',
      label: 'SMA 200',
      borderColor: '#dc3545',
      backgroundColor: '#dc3545',
      data: sma200,
      yAxisID: 'y',
      tension: 0.1,
      pointRadius: 0,
      fill: false,
    }),
    (showBollingerBands && bollingerBands && {
      type: 'line',
      label: 'BB Upper',
      borderColor: '#6c757d',
      backgroundColor: 'rgba(108, 117, 125, 0.1)',
      data: bollingerBands.upper,
      yAxisID: 'y',
      tension: 0.1,
      pointRadius: 0,
      fill: '+1',
    }),
    (showBollingerBands && bollingerBands && {
      type: 'line',
      label: 'BB Middle',
      borderColor: '#6c757d',
      backgroundColor: '#6c757d',
      data: bollingerBands.middle,
      yAxisID: 'y',
      tension: 0.1,
      pointRadius: 0,
      fill: false,
      borderDash: [5, 5],
    }),
    (showBollingerBands && bollingerBands && {
      type: 'line',
      label: 'BB Lower',
      borderColor: '#6c757d',
      backgroundColor: 'rgba(108, 117, 125, 0.1)',
      data: bollingerBands.lower,
      yAxisID: 'y',
      tension: 0.1,
      pointRadius: 0,
      fill: false,
    }),
    (showSupportResistance && support && {
      type: 'line',
      label: 'Support',
      borderColor: '#198754',
      backgroundColor: '#198754',
      data: labels.map(() => support),
      yAxisID: 'y',
      tension: 0,
      pointRadius: 0,
      fill: false,
      borderDash: [6, 4],
    }),
    (showSupportResistance && resistance && {
      type: 'line',
      label: 'Resistance',
      borderColor: '#dc3545',
      backgroundColor: '#dc3545',
      data: labels.map(() => resistance),
      yAxisID: 'y',
      tension: 0,
      pointRadius: 0,
      fill: false,
      borderDash: [6, 4],
    }),
    (showVolume && volumes && {
      type: 'bar',
      label: 'Volume',
      backgroundColor: 'rgba(108, 117, 125, 0.5)',
      borderColor: 'rgba(108, 117, 125, 1)',
      data: volumes,
      yAxisID: 'y1',
    }),
    (showVolumeAnalysis && volumes && {
      type: 'bar',
      label: 'Volume',
      backgroundColor: (context) => {
        if (!context.parsed || !prices) return 'rgba(108, 117, 125, 0.5)';
        const index = context.dataIndex;
        if (index === 0) return 'rgba(108, 117, 125, 0.5)';
        const priceChange = prices[index] - prices[index - 1];
        return priceChange > 0 ? 'rgba(40, 167, 69, 0.7)' : 'rgba(220, 53, 69, 0.7)';
      },
      data: volumes,
      yAxisID: 'y1',
    }),
  ].filter(Boolean);

  const mainChartData = {
    labels: labels,
    datasets: mainChartDatasets,
  };

  const mainChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: title,
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            if (context.dataset.tradeMetaByIndex) {
              const meta = context.dataset.tradeMetaByIndex[context.dataIndex];
              if (meta) {
                const formattedPrice = new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: currency || 'USD'
                }).format(meta.price);
                return `${context.dataset.label}: ${formattedPrice}${meta.timeLabel ? ` (${meta.timeLabel})` : ''}`;
              }
            }
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.dataset.yAxisID === 'y') {
              label += new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(context.parsed.y);
            } else if (context.dataset.yAxisID === 'y1') {
              label += new Intl.NumberFormat('en-US', { style: 'decimal' }).format(context.parsed.y);
            }
            return label;
          },
        },
      },
    },
    scales: {
      x: {
        type: 'category',
        offset: true, // Add offset to align points to the edges
      },
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: 'Price',
        },
        ticks: {
          callback: function (value) {
            return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD', notation: 'compact', compactDisplay: 'short' }).format(value);
          },
        },
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        title: {
          display: true,
          text: 'Volume',
        },
        grid: {
          drawOnChartArea: false, // only show grid lines for the first y-axis
        },
        ticks: {
          callback: function (value) {
            return new Intl.NumberFormat('en-US', { style: 'decimal', notation: 'compact', compactDisplay: 'short' }).format(value);
          },
        },
      },
    },
  };

  // RSI Sub-Chart Data
  const rsiChartData = {
    labels: labels,
    datasets: [
      {
        type: 'line',
        label: 'RSI',
        borderColor: '#28a745', // green
        backgroundColor: '#28a745',
        data: rsi,
        yAxisID: 'rsi-y-axis',
        tension: 0.1,
        pointRadius: 0,
        fill: false,
      },
      // Overbought line
      {
        type: 'line',
        label: 'Overbought (70)',
        borderColor: '#dc3545', // red
        borderDash: [5, 5],
        data: labels.map(() => 70),
        yAxisID: 'rsi-y-axis',
        pointRadius: 0,
        fill: false,
        tooltip: { enabled: false }
      },
      // Oversold line
      {
        type: 'line',
        label: 'Oversold (30)',
        borderColor: '#007bff', // blue
        borderDash: [5, 5],
        data: labels.map(() => 30),
        yAxisID: 'rsi-y-axis',
        pointRadius: 0,
        fill: false,
        tooltip: { enabled: false }
      },
    ].filter(Boolean),
  };

  const rsiChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            label += context.parsed.y.toFixed(2);
            return label;
          },
        },
      },
    },
    scales: {
      x: {
        type: 'category',
        labels: labels, // Ensure x-axis matches main chart
        offset: true, // Add offset to align points to the edges
      },
      'rsi-y-axis': {
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: 'RSI',
        },
        min: 0,
        max: 100,
        ticks: {
          stepSize: 10,
        },
      },
    },
  };

  // MACD Chart Data
  const macdChartData = showMACD && macd ? {
    labels: labels,
    datasets: [
      {
        type: 'line',
        label: 'MACD',
        borderColor: '#007bff',
        backgroundColor: '#007bff',
        data: macd.macd,
        yAxisID: 'macd-y-axis',
        tension: 0.1,
        pointRadius: 0,
        fill: false,
      },
      {
        type: 'line',
        label: 'Signal',
        borderColor: '#dc3545',
        backgroundColor: '#dc3545',
        data: macd.signal,
        yAxisID: 'macd-y-axis',
        tension: 0.1,
        pointRadius: 0,
        fill: false,
      },
      {
        type: 'bar',
        label: 'Histogram',
        backgroundColor: (context) => {
          if (!context.parsed) return '#6c757d';
          return context.parsed.y > 0 ? 'rgba(40, 167, 69, 0.7)' : 'rgba(220, 53, 69, 0.7)';
        },
        data: macd.histogram,
        yAxisID: 'macd-y-axis',
      },
    ],
  } : null;

  const macdChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: false,
      },
    },
    scales: {
      x: {
        type: 'category',
        offset: true,
      },
      'macd-y-axis': {
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: 'MACD',
        },
      },
    },
  };

  // Stochastic Chart Data
  const stochasticChartData = showStochastic && stochastic ? {
    labels: labels,
    datasets: [
      {
        type: 'line',
        label: '%K',
        borderColor: '#007bff',
        backgroundColor: '#007bff',
        data: stochastic.k,
        yAxisID: 'stoch-y-axis',
        tension: 0.1,
        pointRadius: 0,
        fill: false,
      },
      {
        type: 'line',
        label: '%D',
        borderColor: '#dc3545',
        backgroundColor: '#dc3545',
        data: stochastic.d,
        yAxisID: 'stoch-y-axis',
        tension: 0.1,
        pointRadius: 0,
        fill: false,
      },
      {
        type: 'line',
        label: 'Overbought (80)',
        borderColor: '#dc3545',
        borderDash: [5, 5],
        data: labels.map(() => 80),
        yAxisID: 'stoch-y-axis',
        pointRadius: 0,
        fill: false,
      },
      {
        type: 'line',
        label: 'Oversold (20)',
        borderColor: '#28a745',
        borderDash: [5, 5],
        data: labels.map(() => 20),
        yAxisID: 'stoch-y-axis',
        pointRadius: 0,
        fill: false,
      },
    ],
  } : null;

  const stochasticChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: false,
      },
    },
    scales: {
      x: {
        type: 'category',
        offset: true,
      },
      'stoch-y-axis': {
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: 'Stochastic',
        },
        min: 0,
        max: 100,
      },
    },
  };
  const priceChangeChartData = {
    labels: labels,
    datasets: [
      {
        type: 'bar', // Change to bar chart type
        label: '% Change',
        borderColor: context => {
          if (!context.parsed || context.parsed.y === null) return '#ccc'; // Default color for null values
          return context.parsed.y > 0 ? '#28a745' : '#dc3545'; // green for positive, red for negative
        },
        backgroundColor: context => {
          if (!context.parsed || context.parsed.y === null) return '#ccc'; // Default color for null values
          return context.parsed.y > 0 ? '#28a745' : '#dc3545'; // green for positive, red for negative
        },
        data: dailyPercentageChange,
        yAxisID: 'price-change-y-axis',
      },
    ].filter(Boolean),
  };

  const priceChangeChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            label += context.parsed.y.toFixed(2) + '%';
            return label;
          },
        },
      },
    },
    scales: {
      x: {
        type: 'category',
        labels: labels, // Ensure x-axis matches main chart
        offset: true, // Add offset to align points to the edges
      },
              'price-change-y-axis': {
                  type: 'linear',
                  display: true,
                  position: 'left',
                  title: {
                      display: true,
                      text: '% Change',
                  },
                  ticks: {
                      callback: function (value) {
                          return value.toFixed(1) + '%';
                      },
                  },
              },    },
  };

  const atrChartData = {
    labels: labels,
    datasets: [
      {
        type: 'line',
        label: 'ATR (14)',
        borderColor: '#6f42c1',
        backgroundColor: '#6f42c1',
        data: atrSeries,
        yAxisID: 'atr-y-axis',
        tension: 0.1,
        pointRadius: 0,
        fill: false,
      },
    ].filter(Boolean),
  };

  const atrChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: { legend: { position: 'top' }, title: { display: false } },
    scales: {
      x: { type: 'category', offset: true },
      'atr-y-axis': { type: 'linear', display: true, position: 'left', title: { display: true, text: 'ATR' } },
    },
  };

  const adxChartData = {
    labels: labels,
    datasets: [
      {
        type: 'line',
        label: 'ADX (14)',
        borderColor: '#20c997',
        backgroundColor: '#20c997',
        data: adxSeries,
        yAxisID: 'adx-y-axis',
        tension: 0.1,
        pointRadius: 0,
        fill: false,
      },
      {
        type: 'line',
        label: 'Threshold (25)',
        borderColor: '#6c757d',
        borderDash: [5, 5],
        data: labels.map(() => 25),
        yAxisID: 'adx-y-axis',
        pointRadius: 0,
        fill: false,
        tooltip: { enabled: false },
      },
    ].filter(Boolean),
  };

  const adxChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: { legend: { position: 'top' }, title: { display: false } },
    scales: {
      x: { type: 'category', offset: true },
      'adx-y-axis': { type: 'linear', display: true, position: 'left', title: { display: true, text: 'ADX' }, min: 0, max: 100 },
    },
  };

  const obvChartData = {
    labels: labels,
    datasets: [
      {
        type: 'line',
        label: 'OBV',
        borderColor: '#fd7e14',
        backgroundColor: '#fd7e14',
        data: obvSeries,
        yAxisID: 'obv-y-axis',
        tension: 0.1,
        pointRadius: 0,
        fill: false,
      },
    ].filter(Boolean),
  };

  const obvChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: { legend: { position: 'top' }, title: { display: false } },
    scales: {
      x: { type: 'category', offset: true },
      'obv-y-axis': { type: 'linear', display: true, position: 'left', title: { display: true, text: 'OBV' } },
    },
  };

  return (
    <div>
      {/* Main Price Chart */}
      <div style={{ height: '400px' }}>
        <Line data={mainChartData} options={mainChartOptions} />
      </div>
      
      {/* RSI Sub-Chart */}
      {showRSI && (
        <div style={{ height: '150px', marginTop: '10px' }}>
          <Line data={rsiChartData} options={rsiChartOptions} />
        </div>
      )}
      
      {/* MACD Sub-Chart */}
      {showMACD && macdChartData && (
        <div style={{ height: '150px', marginTop: '10px' }}>
          <Line data={macdChartData} options={macdChartOptions} />
        </div>
      )}
      
      {/* Stochastic Sub-Chart */}
      {showStochastic && stochasticChartData && (
        <div style={{ height: '150px', marginTop: '10px' }}>
          <Line data={stochasticChartData} options={stochasticChartOptions} />
        </div>
      )}

      {/* Daily % Change Sub-Chart */}
      {showPriceChange && dailyPercentageChange && dailyPercentageChange.length > 0 && (
        <div style={{ height: '150px', marginTop: '10px' }}>
          <Bar data={priceChangeChartData} options={priceChangeChartOptions} />
        </div>
      )}

      {showATR && atrSeries && atrSeries.length > 0 && (
        <div style={{ height: '150px', marginTop: '10px' }}>
          <Line data={atrChartData} options={atrChartOptions} />
        </div>
      )}

      {showADX && adxSeries && adxSeries.length > 0 && (
        <div style={{ height: '150px', marginTop: '10px' }}>
          <Line data={adxChartData} options={adxChartOptions} />
        </div>
      )}

      {showOBV && obvSeries && obvSeries.length > 0 && (
        <div style={{ height: '150px', marginTop: '10px' }}>
          <Line data={obvChartData} options={obvChartOptions} />
        </div>
      )}
    </div>
  );
};

export default StockPerformanceChart;
